import { useState } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { Check, Loader2, Building2, User, Mail, Phone, Briefcase, Upload, X, Wine, Package, Globe } from "lucide-react";
import api from "../lib/api";

const CHANNELS = [
  { value: "Retail", label: "Retail / Normal Trade" },
  { value: "GDO", label: "GDO" },
  { value: "DO", label: "DO" },
  { value: "Ho.Re.Ca.", label: "Ho.Re.Ca." },
  { value: "Distributore", label: "Distributore" },
];

const FIERE = [
  "Fiera Vinitaly 2026",
  "ProWein 2026",
  "Cibus 2026",
  "SIGEP 2026",
  "Beer Attraction 2026",
  "Altra Fiera",
];

const PRODUCT_CATEGORIES = [
  { id: "milone_spirits", label: "Milone Spirits" },
  { id: "belpaese", label: "Belpaese" },
  { id: "conto_terzi", label: "Conto Terzi" },
];

export default function FierePage() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [businessCard, setBusinessCard] = useState(null);
  const [businessCardPreview, setBusinessCardPreview] = useState(null);
  const [selectedCategories, setSelectedCategories] = useState([]);
  
  const [formData, setFormData] = useState({
    company_name: "",
    contact_name: "",
    email: "",
    phone: "",
    role: "",
    channel: "Retail",
    source_detail: "",
    notes: "",
    language: "IT",  // IT = Italiano, EN = Inglese
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCategoryToggle = (categoryId) => {
    setSelectedCategories(prev => 
      prev.includes(categoryId) 
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setBusinessCard(file);
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setBusinessCardPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeFile = () => {
    setBusinessCard(null);
    setBusinessCardPreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.company_name || !formData.contact_name || !formData.email || !formData.phone || !formData.source_detail) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }

    setIsSubmitting(true);
    try {
      const data = new FormData();
      data.append("company_name", formData.company_name);
      data.append("contact_name", formData.contact_name);
      data.append("email", formData.email);
      data.append("phone", formData.phone);
      data.append("role", formData.role || "");
      data.append("channel", formData.channel);
      data.append("source_detail", formData.source_detail);
      data.append("notes", formData.notes || "");
      data.append("interested_categories", JSON.stringify(selectedCategories));
      data.append("language", formData.language);
      
      if (businessCard) {
        data.append("business_card", businessCard);
      }
      
      await api.post("/fiere/leads", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      
      setIsSuccess(true);
      toast.success("Lead registrato con successo!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nella registrazione");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      company_name: "",
      contact_name: "",
      email: "",
      phone: "",
      role: "",
      channel: "Retail",
      source_detail: formData.source_detail, // Keep the same fair
      notes: "",
      language: "IT",
    });
    setBusinessCard(null);
    setBusinessCardPreview(null);
    setSelectedCategories([]);
    setIsSuccess(false);
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="w-24 h-24 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-green-500/30">
            <Check className="w-12 h-12 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-slate-800 mb-4">Lead Registrato!</h1>
          <p className="text-slate-600 mb-8">
            Grazie! Il contatto di <strong>{formData.company_name}</strong> è stato salvato con successo.
          </p>
          <Button
            onClick={resetForm}
            className="bg-blue-600 text-white hover:bg-blue-700 font-semibold px-8 py-6 text-lg"
          >
            Registra un altro lead
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <div className="py-8 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
            <Wine className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-800 mb-2">
            Raccolta Lead Fiere
          </h1>
          <p className="text-slate-600">
            Registra i contatti acquisiti durante le fiere
          </p>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-2xl mx-auto px-4 pb-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          
          {/* Source Selection */}
          <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
            <h2 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-blue-600" />
              Fonte Lead
            </h2>
            <div className="space-y-2">
              <Label className="text-slate-700">Seleziona la fiera *</Label>
              <Select 
                value={formData.source_detail} 
                onValueChange={(v) => handleChange("source_detail", v)}
              >
                <SelectTrigger className="bg-slate-50 border-slate-200 text-slate-800">
                  <SelectValue placeholder="Seleziona fiera..." />
                </SelectTrigger>
                <SelectContent>
                  {FIERE.map((fiera) => (
                    <SelectItem key={fiera} value={fiera}>{fiera}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {formData.source_detail === "Altra Fiera" && (
                <Input
                  placeholder="Specifica la fiera..."
                  value={formData.source_detail === "Altra Fiera" ? "" : formData.source_detail}
                  onChange={(e) => handleChange("source_detail", e.target.value)}
                  className="bg-slate-50 border-slate-200 text-slate-800 mt-2"
                />
              )}
            </div>
          </div>

          {/* Product Categories */}
          <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
            <h2 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
              <Package className="w-5 h-5 text-amber-600" />
              Categorie di Interesse
            </h2>
            <div className="space-y-3">
              <Label className="text-slate-700">Seleziona una o più categorie</Label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {PRODUCT_CATEGORIES.map((category) => (
                  <label
                    key={category.id}
                    className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      selectedCategories.includes(category.id)
                        ? "border-blue-500 bg-blue-50"
                        : "border-slate-200 bg-slate-50 hover:border-slate-300"
                    }`}
                  >
                    <Checkbox
                      checked={selectedCategories.includes(category.id)}
                      onCheckedChange={() => handleCategoryToggle(category.id)}
                      className="data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                    />
                    <span className={`font-medium ${
                      selectedCategories.includes(category.id) ? "text-blue-700" : "text-slate-700"
                    }`}>
                      {category.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Company Info */}
          <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
            <h2 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-emerald-600" />
              Dati Azienda
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2 space-y-2">
                <Label className="text-slate-700">Nome Azienda *</Label>
                <Input
                  required
                  value={formData.company_name}
                  onChange={(e) => handleChange("company_name", e.target.value)}
                  className="bg-slate-50 border-slate-200 text-slate-800 placeholder:text-slate-400"
                  placeholder="Es: Vini Rossi S.r.l."
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700">Canale</Label>
                <Select 
                  value={formData.channel} 
                  onValueChange={(v) => handleChange("channel", v)}
                >
                  <SelectTrigger className="bg-slate-50 border-slate-200 text-slate-800">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CHANNELS.map((ch) => (
                      <SelectItem key={ch.value} value={ch.value}>{ch.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700 flex items-center gap-1">
                  <Globe className="w-4 h-4" /> Lingua
                </Label>
                <Select 
                  value={formData.language} 
                  onValueChange={(v) => handleChange("language", v)}
                >
                  <SelectTrigger className="bg-slate-50 border-slate-200 text-slate-800">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="IT">
                      <span className="flex items-center gap-2">🇮🇹 Italiano</span>
                    </SelectItem>
                    <SelectItem value="EN">
                      <span className="flex items-center gap-2">🇬🇧 English</span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Contact Info */}
          <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
            <h2 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
              <User className="w-5 h-5 text-blue-600" />
              Dati Contatto
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-700">Nome e Cognome *</Label>
                <Input
                  required
                  value={formData.contact_name}
                  onChange={(e) => handleChange("contact_name", e.target.value)}
                  className="bg-slate-50 border-slate-200 text-slate-800 placeholder:text-slate-400"
                  placeholder="Mario Rossi"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700">Ruolo</Label>
                <Input
                  value={formData.role}
                  onChange={(e) => handleChange("role", e.target.value)}
                  className="bg-slate-50 border-slate-200 text-slate-800 placeholder:text-slate-400"
                  placeholder="Es: Responsabile acquisti"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700 flex items-center gap-1">
                  <Mail className="w-4 h-4" /> Email *
                </Label>
                <Input
                  required
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  className="bg-slate-50 border-slate-200 text-slate-800 placeholder:text-slate-400"
                  placeholder="mario@azienda.it"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700 flex items-center gap-1">
                  <Phone className="w-4 h-4" /> Telefono *
                </Label>
                <Input
                  required
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleChange("phone", e.target.value)}
                  className="bg-slate-50 border-slate-200 text-slate-800 placeholder:text-slate-400"
                  placeholder="+39 333 1234567"
                />
              </div>
            </div>
          </div>

          {/* Business Card Upload */}
          <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
            <h2 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-purple-600" />
              Biglietto da Visita
            </h2>
            
            {businessCardPreview ? (
              <div className="relative">
                <img 
                  src={businessCardPreview} 
                  alt="Biglietto da visita" 
                  className="w-full max-h-48 object-contain rounded-lg bg-slate-100"
                />
                <Button
                  type="button"
                  variant="destructive"
                  size="icon"
                  className="absolute top-2 right-2"
                  onClick={removeFile}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:bg-slate-50 transition-colors">
                <Upload className="w-8 h-8 text-slate-400 mb-2" />
                <span className="text-slate-500 text-sm">Clicca o trascina per caricare</span>
                <span className="text-slate-400 text-xs mt-1">JPG, PNG o PDF</span>
                <input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={handleFileChange}
                  className="hidden"
                />
              </label>
            )}
          </div>

          {/* Notes */}
          <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
            <Label className="text-slate-700">Note aggiuntive</Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => handleChange("notes", e.target.value)}
              className="bg-slate-50 border-slate-200 text-slate-800 placeholder:text-slate-400 mt-2"
              placeholder="Interesse mostrato, prodotti discussi, follow-up da fare..."
              rows={3}
            />
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full h-14 bg-blue-600 hover:bg-blue-700 text-white font-bold text-lg rounded-xl transition-all hover:scale-[1.02] shadow-lg"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Salvataggio...
              </>
            ) : (
              <>
                <Check className="w-5 h-5 mr-2" />
                Salva Lead
              </>
            )}
          </Button>
        </form>
      </div>
    </div>
  );
}
